import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import {
  StarRounded as ProIcon,
  DiamondRounded as UltraIcon,
  CheckCircleRounded as FreeIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import ApiClient from '../../utils/apiClient';
// Note: Using Stripe Checkout instead of Elements for subscriptions

interface Product {
  product_id: string;
  price_id: string;
  name: string;
  full_name: string;
  access_level: string;
  cost: number;
  renewal_cycle: string;
  renewal_cycle_id: string;
}

interface PaymentPageProps {
  selectedProduct: Product;
  onBack: () => void;
  onPaymentSuccess?: () => void;
  onPaymentError?: (error: string) => void;
}

const PaymentForm: React.FC<PaymentPageProps> = ({ 
  selectedProduct, 
  onBack, 
  onPaymentSuccess, 
  onPaymentError 
}) => {
  const theme = useTheme();
  const [processing, setProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const getProductIcon = (accessLevel: string) => {
    switch (accessLevel) {
      case 'pro':
        return <ProIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />;
      case 'ultra':
        return <UltraIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />;
      default:
        return <FreeIcon sx={{ fontSize: 40, color: theme.palette.text.secondary }} />;
    }
  };

  const formatPrice = (cost: number, renewalCycle: string) => {
    if (cost === 0) return 'Free';
    return `£${cost.toFixed(2)} / ${renewalCycle.toLowerCase()}`;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setProcessing(false);
        return;
      }

      // Create checkout session
      const { checkout_url } = await ApiClient.post('api/payments/create-checkout-session', {
        price_id: selectedProduct.price_id
      });

      // Redirect to Stripe Checkout
      window.location.href = checkout_url;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      onPaymentError?.(errorMessage);
      setProcessing(false);
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3} sx={{ maxWidth: '600px', mx: 'auto' }}>
        {/* Back Button */}
        <Button
          startIcon={<BackIcon />}
          onClick={onBack}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to Plans
        </Button>

        {/* Page Title */}
        <Typography component="h1" variant="h4" sx={{ textAlign: 'center' }}>
          Complete Your Purchase
        </Typography>

        {/* Product Summary */}
        <Card sx={{ backgroundColor: theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              {getProductIcon(selectedProduct.access_level)}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {selectedProduct.name.replace(/ (Monthly|Annually)$/, '')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                  {selectedProduct.full_name}
                </Typography>
                <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
                  {formatPrice(selectedProduct.cost, selectedProduct.renewal_cycle_id)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Payment Form */}
        <Card sx={{ backgroundColor: theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Complete Payment
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              You will be redirected to Stripe's secure checkout page to complete your payment.
            </Typography>

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={processing}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {processing ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Redirecting to Payment...
                    </>
                  ) : (
                    `Free for 7 days then pay ${formatPrice(selectedProduct.cost, selectedProduct.renewal_cycle_id)}`
                  )}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

const PaymentPage: React.FC<PaymentPageProps> = (props) => {
  return <PaymentForm {...props} />;
};

export default PaymentPage;
